import React, { useState } from 'react';
import { AN_DATA, ENG_ALL, HEAT_DATA } from '../../constants';

type TimePeriod = '7d' | '30d' | 'all';
type AnView = 'all' | 'playbooks' | 'collaterals';

const TYPE_ICONS: Record<string, string> = { pdf: 'ti-file-type-pdf', ppt: 'ti-presentation', video: 'ti-player-play', url: 'ti-world-www' };

function hcol(v: number, max: number): string {
  const p = v / max;
  if (p < .15) return '#E2E8F0';
  if (p < .35) return '#FDF4E7';
  if (p < .6) return '#F6C87A';
  if (p < .8) return '#C8973A';
  return '#8A6020';
}

export default function AnalyticsScreen() {
  const [time, setTime] = useState<TimePeriod>('7d');
  const [anView, setAnView] = useState<AnView>('all');
  const data = AN_DATA[time];
  const maxBar = Math.max(...data.bars.map(b => b.v));

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '20px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--navy)' }}>Analytics</div>
            <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Engagement & reach — all content</div>
          </div>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--surface)', borderRadius: '8px', padding: '3px' }}>
            {(['7d', '30d', 'all'] as TimePeriod[]).map(t => (
              <button key={t} onClick={() => setTime(t)} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: time === t ? 'white' : 'none', color: time === t ? 'var(--navy)' : 'var(--text3)', boxShadow: time === t ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>
                {t === '7d' ? '7 Days' : t === '30d' ? '30 Days' : 'All Time'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '20px' }}>
          {[
            { icon: 'ti-eye',         label: 'Page Views',       val: data.kpi.views.toLocaleString(),    color: '#1E4E8C', bg: '#EBF8FF', change: '+24%' },
            { icon: 'ti-users',       label: 'Unique Visitors',  val: data.kpi.visitors.toLocaleString(), color: '#276749', bg: '#F0FFF4', change: '+11%' },
            { icon: 'ti-share',       label: 'Links Shared',     val: data.kpi.links.toString(),          color: '#7A5A1A', bg: '#FDF4E7', change: '+7' },
            { icon: 'ti-target',      label: 'Conversions',      val: data.kpi.conv.toString(),           color: '#C53030', bg: '#FFF5F5', change: '+5 leads' },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px 16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`ti ${kpi.icon}`} style={{ color: kpi.color, fontSize: '17px' }} />
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '4px' }}>{kpi.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--navy)', lineHeight: 1 }}>{kpi.val}</div>
                <div style={{ fontSize: '11px', color: '#22C55E', marginTop: '3px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <i className="ti ti-trending-up" /> {kpi.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View switcher */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'var(--surface)', borderRadius: '8px', padding: '3px', width: 'fit-content' }}>
          {(['all', 'playbooks', 'collaterals'] as AnView[]).map(v => (
            <button key={v} onClick={() => setAnView(v)} style={{ padding: '7px 16px', borderRadius: '6px', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: anView === v ? 'white' : 'none', color: anView === v ? 'var(--navy)' : 'var(--text3)', boxShadow: anView === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none', textTransform: 'capitalize' }}>
              {v === 'all' ? 'All Content' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Charts row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
          {/* Bar chart */}
          <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '16px' }}>
              Views per Day <span style={{ fontSize: '10px', fontWeight: '400', color: 'var(--text3)' }}>{anView === 'all' ? 'All content' : anView}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '120px' }}>
              {data.bars.map((b, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                  <div style={{ fontSize: '8px', color: 'var(--text3)', fontWeight: '600', whiteSpace: 'nowrap' }}>{b.v > 999 ? (b.v / 1000).toFixed(1) + 'k' : b.v}</div>
                  <div title={`${b.d}: ${b.v}`} style={{ width: '100%', background: '#C8973A', borderRadius: '3px 3px 0 0', height: `${(b.v / maxBar) * 100}%`, transition: 'height .4s', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#E8B55A')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#C8973A')} />
                  <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '500' }}>{b.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top engagement */}
          <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '12px' }}>Top by Engagement</div>
            {ENG_ALL.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
                <i className={`ti ${TYPE_ICONS[item.type] || 'ti-file'}`} style={{ fontSize: '14px', color: 'var(--text3)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '3px' }}>
                    <div style={{ width: `${item.pct}%`, height: '100%', background: '#C8973A', borderRadius: '2px' }} />
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--navy)' }}>{item.pct}%</div>
                  <div style={{ fontSize: '9px', color: 'var(--text3)' }}>{item.views} views</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics table */}
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '14px', overflow: 'auto' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '12px' }}>All Content</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Name', 'Type', 'Views', 'Engagement', 'Avg Time'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {ENG_ALL.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px', fontWeight: '600', color: 'var(--navy)' }}>{item.name}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '4px', background: 'var(--tag-bg)', color: 'var(--text2)', textTransform: 'uppercase' }}>{item.type}</span>
                  </td>
                  <td style={{ padding: '10px', fontWeight: '700', color: 'var(--navy)' }}>{item.views.toLocaleString()}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '5px', background: 'var(--border)', borderRadius: '3px', maxWidth: '80px' }}>
                        <div style={{ width: `${item.pct}%`, height: '100%', background: '#C8973A', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{item.pct}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px', color: 'var(--text3)' }}>{'3:' + (i * 22 + 10).toString().padStart(2, '0')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Page heatmap */}
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '4px' }}>
            Page Heatmap <span style={{ fontSize: '10px', fontWeight: '400', color: 'var(--text3)' }}>avg seconds per page</span>
          </div>
          {HEAT_DATA.map((item, i) => {
            const mx = Math.max(...item.pages);
            return (
              <div key={i} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text2)', marginBottom: '6px' }}>{item.name}</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {item.pages.map((sec, j) => (
                    <div key={j} title={`Page ${j + 1}: ${sec}s`} style={{ flex: 1, height: '30px', borderRadius: '4px', background: hcol(sec, mx), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: sec / mx > 0.6 ? 'white' : 'var(--text2)', cursor: 'default' }}>
                      {sec}s
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Intensity:</span>
            {[{ color: '#E2E8F0', label: 'None' }, { color: '#F6C87A', label: 'Mid' }, { color: '#8A6020', label: 'Peak' }].map(l => (
              <span key={l.label} style={{ fontSize: '10px', color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: l.color, display: 'inline-block' }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
